// src/pages/legalentity/LegalEntityListPage.tsx
// Legal entities list — search, filter, create. Small domain: one list page,
// one detail/edit page with the verification panel.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, Badge, Input, Alert } from '@react/components';
import { LegalEntityFormDialog } from '@/features/legalentity/LegalEntityFormDialog';
import { errMessage, LEGAL_ENTITY_STATUS_KEYS, LEGAL_ENTITY_STATUS_VARIANT } from '@/features/legalentity/lib';
import { ListLegalEntities } from '@core/api/endpoints/legalentity/list_legal_entities';
import type { LegalEntity } from '@core/types/legalentity';

export const LegalEntityListPage = observer(() => {
  const { t } = useI18n();
  const [items, setItems] = useState<LegalEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await ListLegalEntities({ limit: 100 });
      setItems(res.items ?? []);
    } catch (e) {
      setError(errMessage(e, t('legalentity.list.loadError')));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (e) =>
        e.short_name?.toLowerCase().includes(q) ||
        e.full_name?.toLowerCase().includes(q) ||
        e.primary_tax_id?.toLowerCase().includes(q),
    );
  }, [items, search]);

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <div className="flex items-center justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('legalentity.list.title')}</h1>
        <Button size="sm" className="ml-auto" onClick={() => setCreateOpen(true)}>
          {t('legalentity.create.title')}
        </Button>
      </div>

      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('legalentity.list.searchPlaceholder')} className="max-w-xs" fullWidth={false} />

      {error && (
        <Alert variant="error">
          {error}
          <Button size="sm" variant="secondary" className="ml-2" onClick={load}>
            {t('legalentity.retry')}
          </Button>
        </Alert>
      )}

      {isLoading && items.length === 0 && !error && (
        <div className="space-y-2" aria-busy="true">
          <div className="h-16 animate-pulse rounded-md bg-muted" />
          <div className="h-16 animate-pulse rounded-md bg-muted" />
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('legalentity.list.empty')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('legalentity.list.subtitle')}</p>
          <Button size="sm" className="mt-3" onClick={() => setCreateOpen(true)}>
            {t('legalentity.create.title')}
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((e) => {
          const status = e.status ?? 'LEGAL_ENTITY_STATUS_DRAFT';
          return (
            <Link
              key={e.entity?.id}
              to={`/legalentity/${e.entity?.id}`}
              className="flex items-center justify-between gap-2 rounded-md border border-border p-4 transition-colors hover:border-primary"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-foreground">{e.short_name}</span>
                  {e.is_default && <Badge variant="primary" size="sm">{t('legalentity.detail.default')}</Badge>}
                  {e.is_verified && <Badge variant="success" size="sm">{t('legalentity.detail.verified')}</Badge>}
                </div>
                <p className="truncate text-xs text-muted-foreground">{e.full_name} · {e.primary_tax_id}</p>
              </div>
              <Badge variant={LEGAL_ENTITY_STATUS_VARIANT[status] ?? 'default'} size="sm">
                {t(`legalentity.status.${LEGAL_ENTITY_STATUS_KEYS[status]}`)}
              </Badge>
            </Link>
          );
        })}
      </div>

      <LegalEntityFormDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
    </div>
  );
});
